import {
  WatchCollectionBonusItem,
  WatchCollectionEpisodeItem,
  WatchCollectionItem,
  WatchCollectionMovieItem,
} from '@/services/CollectionsService/types'
import { ProjectType } from '@/services/ProjectsService'
import { Media } from '@/utils/EpisodeUtil'

const WATCHABLE_DEFAULT_AVAILABLE_DATE = '1970-01-01T00:00:00.000Z'

const buildWatchHref = (projectSlug: string | null | undefined, watchableId: string): string => {
  const safeSlug = projectSlug && projectSlug.length ? projectSlug : ''
  return `/watch/${safeSlug}/episode/${watchableId}`
}

const buildVideoHref = (projectSlug: string | null | undefined, videoId: string): string => {
  const safeSlug = projectSlug && projectSlug.length ? projectSlug : ''
  return `/watch/${safeSlug}/videos/bonus/${videoId}`
}

const pickPosterPath = (
  ...images: Array<{ cloudinaryPath?: string | null } | null | undefined>
): string | undefined => {
  const match = images.find((image) => image?.cloudinaryPath)
  return match?.cloudinaryPath ?? undefined
}

const buildProgress = (duration?: number, position?: number): number | undefined => {
  if (!duration || !position) return undefined
  return (position / duration) * 100
}

const isFutureDate = (isoDate?: string | null): boolean => {
  if (!isoDate) return false
  const timestamp = Date.parse(isoDate)
  if (Number.isNaN(timestamp)) {
    return false
  }
  return timestamp > Date.now()
}

export const isWatchCollectionEpisode = (
  node: WatchCollectionItem | null | undefined,
): node is WatchCollectionEpisodeItem => node?.__typename === 'ContentEpisode'

export const isWatchCollectionMovie = (
  node: WatchCollectionItem | null | undefined,
): node is WatchCollectionMovieItem => node?.__typename === 'ContentMovie'

export const isWatchCollectionBonus = (
  node: WatchCollectionItem | null | undefined,
): node is WatchCollectionBonusItem => node?.__typename === 'ContentBonus'

export const mapWatchCollectionEpisodeNodeToMedia = (
  node: WatchCollectionEpisodeItem,
  fallbackProjectSlug: string,
  projectType?: string,
): Media | null => {
  const projectSlug = node.project?.slug ?? fallbackProjectSlug
  const poster = node.posterLandscapeCloudinaryPath ?? pickPosterPath(node.landscapeStillImage) ?? undefined
  if (!poster) return null

  const duration = node.duration ?? undefined
  const position = node.watchPosition?.position ?? undefined
  const publiclyAvailableDate =
    node.watchableAt ??
    node.releaseDate ??
    node.project?.releaseDate ??
    (node.watchableAvailabilityStatus === 'WATCHABLE' ? WATCHABLE_DEFAULT_AVAILABLE_DATE : undefined)
  const prerelease = isFutureDate(publiclyAvailableDate)
  const fallbackProjectType = (projectType as Media['projectType']) ?? 'series'
  const derivedProjectType: Media['projectType'] =
    projectType === 'movie' && node.project?.slug && node.project.slug !== fallbackProjectSlug
      ? 'series'
      : fallbackProjectType

  return {
    guid: node.id,
    title: node.name ?? undefined,
    subtitle: node.subtitle ?? undefined,
    description: node.description ?? undefined,
    poster,
    thumbnailPath: poster,
    projectSlug,
    href: buildWatchHref(projectSlug, node.id),
    episodeNumber: node.episodeNumber ?? undefined,
    duration,
    projectType: derivedProjectType,
    watchableAvailabilityStatus: node.watchableAvailabilityStatus ?? null,
    watchableType: 'EPISODE',
    episodeSlug: node.slug ?? undefined,
    progress: buildProgress(duration, position),
    publiclyAvailableDate,
    prerelease,
  }
}

export const mapWatchCollectionMovieNodeToMedia = (node: WatchCollectionMovieItem): Media | null => {
  const projectSlug = node.project?.slug
  const poster =
    node.posterLandscapeCloudinaryPath ??
    pickPosterPath(node.landscapeStillImage, node.landscapeAngelImage, node.landscapeTitleImage)

  if (!projectSlug || !poster) return null

  const duration = node.duration ?? undefined
  const position = node.watchPosition?.position ?? undefined
  const subtitle = node.subtitle ?? undefined
  const publiclyAvailableDate =
    node.watchableAt ??
    node.releaseDate ??
    node.project?.releaseDate ??
    (node.watchableAvailabilityStatus === 'WATCHABLE' ? WATCHABLE_DEFAULT_AVAILABLE_DATE : undefined)
  const prerelease = isFutureDate(publiclyAvailableDate)

  return {
    guid: node.id,
    title: node.name ?? undefined,
    subtitle,
    description: node.description ?? undefined,
    poster,
    thumbnailPath: poster,
    projectSlug,
    href: buildWatchHref(projectSlug, node.id),
    duration,
    projectType: 'movie',
    watchableAvailabilityStatus: node.watchableAvailabilityStatus ?? null,
    watchableType: 'MOVIE',
    progress: buildProgress(duration, position),
    publiclyAvailableDate,
    prerelease,
  }
}

export const mapWatchCollectionBonusNodeToMedia = (node: WatchCollectionBonusItem): Media | null => {
  const projectSlug = node.project?.slug

  if (!projectSlug) return null

  const poster = pickPosterPath(node.landscapeStillImage) ?? node.posterLandscapeCloudinaryPath

  if (!poster) return null

  const duration = node.duration ?? undefined
  const position = node.watchPosition?.position ?? undefined
  const publiclyAvailableDate =
    node.watchableAt ??
    node.releaseDate ??
    node.project?.releaseDate ??
    (node.watchableAvailabilityStatus === 'WATCHABLE' ? WATCHABLE_DEFAULT_AVAILABLE_DATE : undefined)
  const prerelease = isFutureDate(publiclyAvailableDate)

  return {
    guid: node.id,
    title: node.name ?? undefined,
    subtitle: node.subtitle ?? undefined,
    description: node.description ?? undefined,
    poster,
    thumbnailPath: poster,
    projectSlug,
    href: buildVideoHref(projectSlug, node.id),
    duration,
    projectType: (node.project?.projectType as ProjectType) ?? undefined,
    watchableAvailabilityStatus: node.watchableAvailabilityStatus ?? null,
    watchableType: 'BONUS_CONTENT',
    progress: buildProgress(duration, position),
    publiclyAvailableDate,
    prerelease,
  }
}
